import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import MetricCard from "../components/MetricCard";
import { getStatsRevenue, getStatsSubscriptions } from "../api/adminApi";
import type { StatsRevenueResponse, StatsSubscriptionsResponse } from "../types/admin";

function Revenue() {
  const [revenueData, setRevenueData] = useState<StatsRevenueResponse | null>(null);
  const [subsData, setSubsData] = useState<StatsSubscriptionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      setLoading(true);
      const [revenue, subs] = await Promise.all([getStatsRevenue(), getStatsSubscriptions()]);
      setRevenueData(revenue);
      setSubsData(subs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load revenue data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={loadData} style={{ marginTop: "1rem" }}>
          Retry
        </button>
      </div>
    );
  }

  if (!revenueData || !subsData) return null;

  const { summary, daily } = revenueData;
  const chartData = [...daily].reverse();

  // Tier distribution for pie chart
  const tierData = [
    { name: "Free", value: subsData.current.free, color: "#64748b" },
    { name: "Basic", value: subsData.current.basic, color: "#3b82f6" },
    { name: "Standard", value: subsData.current.standard, color: "#8b5cf6" },
    { name: "Premium", value: subsData.current.premium, color: "#22c55e" },
  ].filter((t) => t.value > 0);

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  };

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Revenue & Subscriptions</h1>
        <p className="page-subtitle">
          {revenueData.period.start} to {revenueData.period.end}
        </p>
      </header>

      {/* Summary Metrics */}
      <div className="metrics-grid">
        <MetricCard label="Current MRR" value={formatCurrency(summary.currentMrrCents)} />
        <MetricCard label="Period Revenue" value={formatCurrency(summary.periodRevenueCents)} />
        <MetricCard label="New Subscriptions" value={summary.newSubscriptions} />
        <MetricCard
          label="Net Subscriptions"
          value={summary.netSubscriptions}
          emphasis={summary.netSubscriptions < 0 ? "danger" : summary.netSubscriptions > 0 ? "success" : "default"}
        />
      </div>

      {/* MRR Trend Chart */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">MRR Over Time</h3>
        </div>
        <div className="card-body">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                  tickFormatter={(value) => value?.slice(5) || ""}
                />
                <YAxis
                  stroke="#94a3b8"
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 100).toFixed(0)}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #475569",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#f1f5f9" }}
                  formatter={(value: number) => formatCurrency(value)}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="mrrCents"
                  name="MRR"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tier Distribution and Trial Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
        {/* Pie Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Tier Distribution</h3>
          </div>
          <div className="card-body">
            <div className="chart-container" style={{ height: "250px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tierData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {tierData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e293b",
                      border: "1px solid #475569",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#f1f5f9" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Trial Metrics */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Trial Metrics</h3>
          </div>
          <div className="card-body">
            <div className="metrics-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <MetricCard label="Total Trials" value={subsData.trialMetrics.totalTrials} />
              <MetricCard label="Conversions" value={subsData.trialMetrics.conversions} />
              <MetricCard label="Expirations" value={subsData.trialMetrics.expirations} />
              <MetricCard
                label="Conversion Rate"
                value={`${subsData.trialMetrics.conversionRate.toFixed(1)}%`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Daily Revenue Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Daily Revenue</h3>
        </div>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>MRR</th>
                <th>Revenue</th>
                <th>New</th>
                <th>Cancelled</th>
                <th>Upgrades</th>
                <th>Downgrades</th>
              </tr>
            </thead>
            <tbody>
              {daily.slice(0, 14).map((day) => (
                <tr key={day.date}>
                  <td>{day.date}</td>
                  <td>{formatCurrency(day.mrrCents)}</td>
                  <td style={{ color: day.revenueCents > 0 ? "var(--accent-success)" : "inherit" }}>
                    {formatCurrency(day.revenueCents)}
                  </td>
                  <td style={{ color: "var(--accent-success)" }}>+{day.newSubscriptions}</td>
                  <td style={{ color: day.cancellations > 0 ? "var(--accent-danger)" : "inherit" }}>
                    {day.cancellations > 0 ? `-${day.cancellations}` : day.cancellations}
                  </td>
                  <td>{day.upgrades}</td>
                  <td>{day.downgrades}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Revenue;
